import { type FC, type ReactNode, useCallback } from 'react';
import { useDialogSessionOptional } from '@/features/DialogSession';
import { cn, formatDateTime } from '@/shared/lib';
import { CHAT_MESSAGE_CLOUD_HIGHLIGHT_TARGET_CLASS } from '@/shared/ui/Chat';
import { FEED_ITEM_VIEWS, type FeedItemKind } from '../model/feedItem';
import { RoleLabel } from './RoleLabel';

type Props = {
  /** ID элемента переписки: им облако помечено как цель перехода к сообщению */
  messageId: number;
  /** Вид элемента переписки — по нему подпись и цвета облака */
  kind: FeedItemKind;
  /** Имя участника; неизвестен или не показывается у этого вида — нет */
  personName?: string | null;
  /** Момент отправки элемента */
  createdAt: string;
  /** Содержимое облака */
  children: ReactNode;
};

/**
 * Облако элемента переписки по левому краю во всю ширину ленты при любой её ширине. Над ним — подпись
 * вида, имя участника и справа время отправки: интервала у элемента переписки нет, только момент отправки.
 *
 * Что в строке над облаком не уместилось, переносится — сначала время, затем имя, — а не сокращается.
 *
 * Облако вместе со строкой над ним — цель перехода к сообщению: его регистрирует сессия диалога.
 * Выделяется при этом само облако, без строки над ним.
 */
export const FeedBubble: FC<Props> = ({ messageId, kind, personName, createdAt, children }) => {
  const { bubbleClassName, hasSender } = FEED_ITEM_VIEWS[kind];
  const registerMessageElement = useDialogSessionOptional()?.registerMessageElement;

  const attachMessageElement = useCallback(
    (element: HTMLDivElement | null) => {
      if (!element || !registerMessageElement) return;
      return registerMessageElement(messageId, element);
    },
    [registerMessageElement, messageId],
  );

  return (
    <div ref={attachMessageElement} data-message-id={messageId} className="flex w-full min-w-0 flex-col gap-1">
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="flex min-w-0 items-center gap-2">
          <RoleLabel kind={kind} />
          {hasSender && personName && (
            <span className="min-w-0 wrap-break-word font-semibold text-(--color-chat-cloud-sender-name)">
              {personName}
            </span>
          )}
        </span>
        <span className="ml-auto shrink-0 font-semibold text-muted-foreground">{formatDateTime(createdAt)}</span>
      </div>
      <div
        className={cn(
          'flex min-w-0 flex-col gap-2 rounded-lg border border-(--color-chat-cloud-border) px-3.5 py-2.5 text-sm text-foreground',
          CHAT_MESSAGE_CLOUD_HIGHLIGHT_TARGET_CLASS,
          bubbleClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
};
